import React, { useState, useContext, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserContext } from "./context/UserContext";
import logo from "../assets/goatt_web.png";

/* ─── Route map ─────────────────────────────────────────────────────────── */
const NETWORKS = {
  tutor: {
    home: "https://tn.goatteachers.com/",
    login: "/login",
    signup: "/signup",
  },
  rating: {
    home: "/",
    login: "/login",
    signup: "/signup",
  },
};

/* ─── Helpers ────────────────────────────────────────────────────────────── */
function formatUserName(name) {
  const first = name?.trim()?.split(" ")[0] || "Profile";
  return first.length > 10 ? first.slice(0, 8) + "…" : first;
}

function ExternalIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      className={`h-3.5 w-3.5 flex-shrink-0 ${className}`}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M4.5 2A1.5 1.5 0 003 3.5v9A1.5 1.5 0 004.5 14h9a1.5 1.5 0 001.5-1.5v-3a.75.75 0 00-1.5 0v3h-9v-9h3a.75.75 0 000-1.5h-3zm8.03.22a.75.75 0 00-1.06 1.06L13.19 5H9.75a.75.75 0 000 1.5h3.44l-1.72 1.72a.75.75 0 101.06 1.06l3-3a.75.75 0 000-1.06l-3-3z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/* ─── Dropdown content ───────────────────────────────────────────────────── */
const DROPDOWN_SECTIONS = [
  {
    heading: "Home",
    items: [{ label: "Home", href: NETWORKS.rating.home, external: true }],
  },
  {
    heading: "Other Products",
    items: [
      { label: "Tutor Network", href: NETWORKS.tutor.home, external: true },
    ],
  },
  {
    heading: "Login",
    items: [
      {
        label: "Sign up",
        href: NETWORKS.tutor.signup,
        external: true,
      },
      {
        label: "Log in",
        href: NETWORKS.tutor.login,
        external: true,
      },
    ],
  },
];

function MenuItem({ item, onClick, dark = false }) {
  const baseClass = dark
    ? "group flex items-center justify-between rounded-xl px-3 py-3 text-[15px] font-medium text-white transition-colors hover:bg-white/10"
    : "group flex items-center justify-between rounded-lg px-2 py-2 text-[14px] font-medium text-gray-800 transition-colors duration-150 hover:bg-gray-50 hover:text-black";

  if (item.external) {
    return (
      <a
        href={item.href}
        rel="noopener noreferrer"
        onClick={onClick}
        className={baseClass}
      >
        <span>{item.label}</span>
        <ExternalIcon
          className={
            dark
              ? "text-gray-500 transition-colors group-hover:text-gray-300"
              : "text-gray-400 transition-colors group-hover:text-gray-600"
          }
        />
      </a>
    );
  }

  return (
    <Link to={item.to} onClick={onClick} className={baseClass}>
      <span>{item.label}</span>
    </Link>
  );
}

/* ─── Tutor Network Dropdown ─────────────────────────────────────────────── */
function TutorDropdown({ open, onClose, onOpen, sections }) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    }

    function handleEscape(e) {
      if (e.key === "Escape") onClose();
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  return (
    <div
      ref={panelRef}
      className={`absolute right-0 top-full z-50 w-64 origin-top-right pt-2 transition-all duration-200 ${
        open
          ? "pointer-events-auto scale-100 opacity-100"
          : "pointer-events-none scale-95 opacity-0"
      }`}
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
    >
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl">
        {sections.map((section, si) => (
          <div key={section.heading}>
            {si > 0 && <div className="mx-4 border-t border-gray-100" />}
            <div className="px-4 pb-2 pt-4">
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">
                {section.heading}
              </p>
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={`${section.heading}-${item.label}`}>
                    <MenuItem item={item} onClick={onClose} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
        <div className="h-2" />
      </div>
    </div>
  );
}

/* ─── Main Header ────────────────────────────────────────────────────────── */
export default function Header() {
  const { user, logoutUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const dropdownTimeoutRef = useRef(null);

  const clearDropdownTimeout = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
  };

  const openDropdown = () => {
    clearDropdownTimeout();
    setDropdownOpen(true);
  };

  const closeDropdownWithDelay = () => {
    clearDropdownTimeout();
    dropdownTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 180);
  };

  const closeDropdownImmediately = () => {
    clearDropdownTimeout();
    setDropdownOpen(false);
  };

  const closeMobile = () => setMobileOpen(false);

  const closeAll = () => {
    closeDropdownImmediately();
    closeMobile();
  };

  useEffect(() => {
    return () => clearDropdownTimeout();
  }, []);

  useEffect(() => {
    function handleEscape(e) {
      if (e.key === "Escape") {
        closeAll();
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleLogout = async () => {
    try {
      logoutUser();
      closeAll();
      navigate("/login");
    } catch (err) {
      console.error("Logout error:", err.message);
    }
  };

  const goToProfile = () => {
    if (user) {
      navigate("/userprofile");
      closeAll();
    }
  };

  const navLinks = [
    { label: "হোম", to: "/" },
    { label: "রেটিং নেটওয়ার্ক", to: "/rating-network" },
    { label: "আমাদের সম্পর্কে", to: "/about" },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50 bg-black text-white">
      <div className="mx-auto flex h-[60px] max-w-[1400px] items-center justify-between gap-4 px-4 md:px-8">
        {/* Logo */}
        <Link to="/" className="flex flex-shrink-0 items-center">
          <img
            src={logo}
            alt="GOATT"
            className="h-[40px] w-auto object-contain sm:h-[46px]"
          />
        </Link>

        {/* Desktop Nav 
        <nav className="hidden items-center gap-7 text-[15px] font-medium tracking-wide lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="font-['Hind_Siliguri',sans-serif] text-gray-300 transition-colors duration-150 hover:text-white"
            >
              {link.label}
            </Link>
          ))}
        </nav> */}

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Desktop split button */}
          <div
            className="relative hidden items-center lg:flex"
            onMouseEnter={openDropdown}
            onMouseLeave={closeDropdownWithDelay}
          >
            <a
              href={NETWORKS.tutor.home}
              rel="noopener noreferrer"
              className="flex h-9 items-center rounded-l-xl bg-white pl-4 pr-3 text-[13.5px] font-semibold text-black transition-colors duration-150 hover:bg-gray-100 active:bg-gray-200"
            >
              Try Tutor Network
            </a>

            <span className="h-5 w-px flex-shrink-0 bg-gray-300" />

            <button
              type="button"
              onMouseEnter={openDropdown}
              onClick={() => {
                if (dropdownOpen) {
                  closeDropdownImmediately();
                } else {
                  openDropdown();
                }
              }}
              aria-expanded={dropdownOpen}
              aria-haspopup="menu"
              aria-label="Open Tutor Network menu"
              className="flex h-9 w-9 items-center justify-center rounded-r-xl bg-white text-black transition-colors duration-150 hover:bg-gray-100 active:bg-gray-200 focus-visible:outline-none"
            >
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                className={`h-4 w-4 text-gray-600 transition-transform duration-200 ${
                  dropdownOpen ? "rotate-180" : "rotate-0"
                }`}
              >
                <path
                  fillRule="evenodd"
                  d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            <TutorDropdown
              open={dropdownOpen}
              onClose={closeDropdownWithDelay}
              onOpen={openDropdown}
              sections={DROPDOWN_SECTIONS}
            />
          </div>

          {/* Desktop profile */}
          {user && (
            <button
              onClick={goToProfile}
              className="hidden h-9 items-center justify-center rounded-xl bg-gradient-to-r from-emerald-500 via-green-500 to-lime-400 px-4 text-[13.5px] font-semibold text-white transition-opacity duration-150 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 lg:flex"
            >
              {user.name ? formatUserName(user.name) : "Profile"}
            </button>
          )}

          {/* Mobile toggle */}
          <button
            type="button"
            className="flex h-10 w-10 flex-col items-center justify-center rounded-lg transition-colors hover:bg-white/10 lg:hidden"
            onClick={() => setMobileOpen((p) => !p)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            <span
              className={`block h-[2px] w-[18px] bg-white transition-all duration-300 ${
                mobileOpen ? "translate-y-[5px] rotate-45" : ""
              }`}
            />
            <span
              className={`my-[4px] block h-[2px] w-[18px] bg-white transition-all duration-300 ${
                mobileOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block h-[2px] w-[18px] bg-white transition-all duration-300 ${
                mobileOpen ? "-translate-y-[7px] -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out lg:hidden ${
          mobileOpen ? "max-h-[90vh] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="border-t border-white/10 bg-black px-4 py-4">
          <div className="max-h-[calc(90vh-60px)] space-y-4 overflow-y-auto pb-4">
            {/* Primary CTA */}
            <a
              href={NETWORKS.tutor.home}
              rel="noopener noreferrer"
              onClick={closeMobile}
              className="flex items-center justify-between rounded-2xl bg-white px-4 py-3.5 text-[15px] font-semibold text-black shadow-sm transition-colors hover:bg-gray-100"
            >
              <span>Try Tutor Network</span>
              <ExternalIcon className="text-gray-500" />
            </a>

            {/* Structured sections */}
            <div className="overflow-hidden rounded-2xl border border-white/15 bg-[#101010]">
              {DROPDOWN_SECTIONS.map((section, si) => (
                <div key={section.heading}>
                  {si > 0 && <div className="mx-4 border-t border-white/10" />}
                  <div className="px-4 py-4">
                    <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                      {section.heading}
                    </p>
                    <ul className="space-y-1">
                      {section.items.map((item) => (
                        <li key={`${section.heading}-${item.label}`}>
                          <MenuItem
                            item={item}
                            onClick={closeMobile}
                            dark={true}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile nav */}
            <div className="space-y-1"></div>

            <div className="h-px bg-white/10" />

            {/* Mobile auth */}
            {user ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  onClick={goToProfile}
                  className="w-full rounded-xl bg-gradient-to-r from-emerald-500 via-green-500 to-lime-400 px-4 py-3 text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
                >
                  {user.name ? formatUserName(user.name) : "Profile"}
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full rounded-xl bg-white/10 px-4 py-3 font-['Hind_Siliguri',sans-serif] text-[14px] font-medium text-white transition-colors hover:bg-white/20"
                >
                  লগ আউট
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3"></div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
